import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import styles from 'components/UI/V3/Drawer/Drawer.module.scss';
import { DrawerProvider } from 'components/UI/V3/Drawer/DrawerContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const ANIMATION_DURATION = 400;

interface DrawerProps {
    children: React.ReactNode;
    onClose?: () => void;
}

export const Drawer: React.FC<DrawerProps> = ({ children, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Auto-animate in on mount
    useEffect(() => {
        setIsVisible(true);
        requestAnimationFrame(() => {
            setIsAnimating(true);
        });
    }, []);

    const closeHandler = useCallback(() => {
        setIsAnimating(false);

        const timeout = setTimeout(() => {
            setIsVisible(false);
            onClose?.();
        }, ANIMATION_DURATION);

        return () => clearTimeout(timeout);
    }, [onClose]);

    const contextValue = useMemo(() => ({ close: closeHandler }), [closeHandler]);

    if (!isVisible) return null;

    return createPortal(
        <DrawerProvider value={contextValue}>
            <div
                className={clsx(styles.root, { [styles.open]: isAnimating })}
                style={
                    {
                        '--animation-duration': `${ANIMATION_DURATION}ms`
                    } as React.CSSProperties
                }
            >
                <div className={styles.drawer}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={closeHandler}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>

                    <div className={styles.wrapper}>{children}</div>
                </div>

                <div
                    className={styles.backdrop}
                    onClick={closeHandler}
                />
            </div>
        </DrawerProvider>,
        document.body
    );
};
